import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import {
  ButtonHTMLAttributes,
  forwardRef,
  HTMLAttributes,
  SVGProps,
} from "react";

import { cn } from "./utils";

// ---------------------------------------------------------------------------
// Inline loading spinner (no external dependency)
// ---------------------------------------------------------------------------

function LoadingSpinner({
  size = 5,
  ...props
}: { size?: number } & SVGProps<SVGSVGElement>) {
  const px = size * 4;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn(`h-${size} w-${size} animate-spin`)}
      aria-hidden="true"
      {...props}
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------------

export type ButtonSize =
  | "default"
  | "xs"
  | "sm"
  | "lg"
  | "xl"
  | "icon"
  | "icon-xs"
  | "icon-sm"
  | "icon-lg"
  | "tinyIcon"
  | "tinyButton"
  | "pill";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent text-gray-800 hover:text-gray-600",
        destructiveOutline:
          "border border-red-300 bg-background shadow-sm hover:bg-red-50 text-destructive hover:text-red-600",
        secondaryOutline:
          "border border-blue-300 bg-background shadow-sm hover:bg-blue-50 text-gray-800 hover:text-blue-600",
        blueOutline:
          "border border-blue-500 bg-background shadow-sm hover:bg-blue-50 text-blue-600 hover:text-blue-700",
        secondary:
          "bg-gray-50 border border-gray-300 text-secondary-foreground shadow-sm hover:bg-secondary/80",
        secondaryLight:
          "bg-blue-100 text-blue-700 shadow-sm hover:bg-blue-100/80 hover:text-blue-600",
        secondaryLightOutline:
          "border border-blue-300 bg-blue-50 text-blue-600 shadow-sm hover:bg-blue-50/80 hover:border-blue-400",
        ghost: "hover:bg-black/5 hover:text-gray-600 text-gray-800",
        ghostGray:
          "bg-black/5 hover:bg-black/10 text-gray-800 hover:text-gray-900",
        ghostBlue:
          "text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700",
        link: "text-blue-600 hover:text-blue-400 underline-offset-4",
        inlineLink:
          "text-sm font-semibold text-[#1D4ED8] hover:text-[#1D4ED8]/90 !p-0",
        destructiveLink: "text-red-600 hover:text-red-400 underline-offset-4",
        blue: "bg-blue-100 text-blue-700 hover:bg-blue-200",
        pill: "border border-gray-200 bg-white text-gray-400 shadow-sm transition-colors duration-150 hover:border-gray-300 hover:text-gray-600 hover:shadow focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-1",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        xl: "h-12 rounded-lg px-10 text-lg",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
        tinyIcon: "size-7",
        tinyButton: "h-5 px-2 text-xs",
        pill: "rounded-full px-2.5 py-0.5 text-xs gap-1",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type ExtendedButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof buttonVariants>;

export interface ButtonProps extends ExtendedButtonProps {
  asChild?: boolean;
  isLoading?: boolean;
  size?: ButtonSize;
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      disabled,
      size,
      variant,
      asChild = false,
      isLoading = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? (Slot.Root as unknown as "button") : "button";
    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={disabled || isLoading}
        ref={ref}
        {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {isLoading ? <LoadingSpinner size={5} /> : children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

// ---------------------------------------------------------------------------
// DivButton
// ---------------------------------------------------------------------------

const DivButton = forwardRef<HTMLDivElement, ButtonProps>(
  (
    {
      className,
      disabled,
      size,
      variant,
      asChild = false,
      isLoading = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? (Slot.Root as unknown as "div") : "div";
    const isDisabled = disabled || isLoading;
    return (
      <Comp
        className={cn(
          "cursor-pointer",
          buttonVariants({ variant, size, className }),
          isDisabled && "pointer-events-none opacity-50",
        )}
        aria-disabled={isDisabled}
        ref={ref}
        {...(props as HTMLAttributes<HTMLDivElement>)}
      >
        {isLoading ? <LoadingSpinner size={5} /> : children}
      </Comp>
    );
  },
);
DivButton.displayName = "DivButton";

export { Button, DivButton };
